import { Component, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { TranslateService } from './service';

import { ILocalizationDictionary, ITranslation, eLanguageCodes, LanguageCode } from './interfaces';
import { Router } from '@angular/router';
import { TokenService } from '../../services/token.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmationComponent, DeleteConfirmationSettings } from '../shared/dialogs/delete-confirmation';
import { SharedUpdateService } from '../shared/dialogs/update.service';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ILocalizedDictionaryDetailsDialog, LocalizedDictionaryDetails } from './localization-details';

export interface ITranslationRowDynamic {
	keyword: string;
	[languageCode: string]: string; // 'en', 'es', etc.
}

@Component({
	moduleId: module.id,
	selector: 'localization',
	templateUrl: 'localization.component.html',
	styleUrls: ['localization.component.scss']
})

export class LocalizationComponent implements OnInit, OnDestroy {
	@ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
	@ViewChild(MatSort, { static: true }) sort: MatSort;
	public dataSource: MatTableDataSource<ITranslationRowDynamic>;
	public displayedColumns: string[] = [];

	private _languageCodes: LanguageCode[];
	private _translations: ITranslation[];

	private _subscriptions$: Subject<boolean>;
	private _selectedTerm: ITranslation;

	constructor(
		private _dialog: MatDialog,
		private _router: Router,
		private _tokenService: TokenService,
		private translateService: TranslateService,
		private _updateService: SharedUpdateService
		) {

		if (this._tokenService.isAuthenticated() === false) {
			this._router.navigate(['login']);
		}

		this._languageCodes = new Array();
		this._translations = new Array();
	}

	ngOnInit(): void {
		this._subscriptions$ = new Subject();
		this._configureTableSorting();
		this._list();

		this._updateService.delete$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this._delete(this._selectedTerm);
		});

		this._updateService.saveComplete$.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
			this.loadLocalizedDictionaries(false);
		});
	}

	ngOnDestroy(): void {
		if (!this._subscriptions$) {
			return;
		}

		this._subscriptions$.next(true);
		this._subscriptions$.complete();
	}

	private _configureTableSorting(): void {
		if (this.sort) {
			if (this.sort.sortChange) {
				this.sort.sortChange.pipe(takeUntil(this._subscriptions$)).subscribe(() => {
					while (this.paginator.hasPreviousPage()) {
						this.paginator.previousPage();
					}
				});
			}

			this.sort.sort({ id: 'System Label', start: 'asc', disableClear: false });
		}
	}

	private _list(): void {
		this.translateService.loadLanguages()
			.then(res => {
				this._languageCodes = res;
				this.loadLocalizedDictionaries();
			});
	}

	private _buildTableRows(translations: ITranslation[], languageCodes: LanguageCode[]): ITranslationRowDynamic[]  {
		if (!translations || !languageCodes) {
			return [];
		}

		return translations.map(t => {
			let row: ITranslationRowDynamic = { keyword: t.keyword };

			languageCodes.forEach(languageCode => {
				let dictionary: ILocalizationDictionary;

				dictionary = t.translations[languageCode.Id];
				row[languageCode.Code] = (dictionary && dictionary.Word) ? dictionary.Word : '';
			});

			return row;
		});
	}

	public search(query: string): void {
		if (!this.dataSource) {
			return;
		}

		if (query === null) {
			query = '';
		}

		query = query.trim();
		query = query.toLowerCase();

		this.dataSource.filter = query;
	}

	private loadLocalizedDictionaries(useLocalStorage = true) {
		this.translateService.loadTranslations(useLocalStorage)
			.then(res => {
				this._translations = res
				this._setDataSource(this._buildTableRows(this._translations, this._languageCodes));
				this.displayedColumns = ['keyword', ...this._languageCodes.map(code => code.Code), 'Action'];
			});
	}

	private _setDataSource(results: ITranslationRowDynamic[]): void {
		if (!this.dataSource) {
			this.dataSource = new MatTableDataSource(results);
			this.dataSource.paginator = this.paginator;
			this.dataSource.sort = this.sort;
		} else {
			this.dataSource.data = results;
		}
	}

	public create(): void {
		let data: ILocalizedDictionaryDetailsDialog;

		data = {
			translation: {
				keyword: null,
				translations: []
			},
			languageCodes: this._languageCodes
		};

		this._dialog.open(LocalizedDictionaryDetails, {
			disableClose: true,
			data: data
		});
	}

	public edit(row: ITranslationRowDynamic): void {
		let data: ILocalizedDictionaryDetailsDialog;
		let translation: ITranslation;

		translation = this._translations.find(o => o.keyword === row.keyword);

		data = {
			translation: translation,
			languageCodes: this._languageCodes
		};

		this._dialog.open(LocalizedDictionaryDetails, {
			disableClose: true,
			data: data
		});
	}

	/**
	 * Open delete confirmation dialog
	 * Find and cache the chosen term for deletion
	 * @param translation
	 */
	public delete(translation: ITranslationRowDynamic): void {
		let data: DeleteConfirmationSettings;

		data = {
			objectName: 'System Label',
			specificName: translation.keyword,
			additionalMessage: 'All translations associated with this label will also be deleted.'
		}

		this._selectedTerm = this._translations.find(o => o.keyword === translation.keyword);

		this._dialog.open(DeleteConfirmationComponent, {
			disableClose: true,
			data: data
		});
	}

	private _delete(translation: ITranslation): void {
		let ids: number[] = new Array();

		for (let languageCode of this._languageCodes) {
			if (!translation.translations[languageCode.Id]) {
				continue;
			}

			ids.push(translation.translations[languageCode.Id].Id);
		}

		this.translateService.deleteTranslation(ids)
			.then(() => {
				this._updateService.deleteComplete();
				this.loadLocalizedDictionaries(false);
			});
	}
}

